
import { ref } from "vue";
import { Options, Vue } from "vue-class-component";
import { Inject, Watch } from "vue-property-decorator";
import FileSelector from '../file-selector/index.vue';
import FileDragAndDrop from "../file-drag-and-drop/index.vue";
import HelpScreen from '../help-screen/index.vue';

import { CommitteeView, PersonalFile } from "@/modules/typescript-cmpApi-client/models";
import store from "@/store";
import { CMPApi } from "@/modules/cmp-api";
import { AppInsights } from "@/appInsights";
import { EventEmitter } from "events";
import AlertBox from '../alert-box/index.vue';
import moment from "moment";
import { orderBy } from "lodash";
import { RouteN } from "@/constants/route-name";
import HelpText from '../../assets/config/help-text.json';
import FullScreenFileViewer from '../full-screen-file-viewer/index.vue';
import LoadingIndicator from '@/components/loading-indicator/index.vue';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

@Options({
  components: {
    FileSelector,
    FileDragAndDrop,
    AlertBox,
    HelpScreen,
    FullScreenFileViewer,
    QuillEditor,
    LoadingIndicator,
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    if (to.name === RouteN.CMP.Dashboard.n && this.unsavedNote) {
      this.showUnsavedAlertBox = true;
      next(false);
    } else {
      next();
    }
  }
})

export default class CommitteeFiles extends Vue {

  @Inject({ from: 'apiService', default: new CMPApi() })
  private apiService!: CMPApi;

  @Inject({ from: 'eventController', default: new EventEmitter })
  private eventCtrl!: EventEmitter;

  private ai = new AppInsights;

  private fileSource = '';
  private iframeRef = ref<HTMLElement | null>();
  private fileId = '';
  private committeeId = '';
  private activeTab = 1;
  private isSubmitting = false;

  private selectedDocument = {} as File;

  private showHelpScreen = false;
  private showNote = true;

  private meetingNote = '';
  private editorContent = '';

  private agendaFiles = [];
  private agendaSupportingFiles = [];
  private personalMeetingFiles = [];
  private myCommittees: CommitteeView[] = [];
  private currentCommittee: CommitteeView = {};
  private currentFile = {};

  private showFileDeleteAlertBox = false;
  private fileToDelete = 0;

  private editor: any;
  private unsavedNote = false;
  private showUnsavedAlertBox = false;

  private showFullScreenFileViewer = false;
  private showEditor = false;

  private toolbarOptions = [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    ['blockquote', 'code-block'],

    // [{ 'header': 1 }, { 'header': 2 }],               // custom button values
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    // [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
    [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent
    // [{ 'direction': 'rtl' }],                         // text direction

    // [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    ['link'],
    // [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    // [{ 'font': [] }],
    [{ 'align': [] }],

    ['clean']                                         // remove formatting button
  ];

  private blankToolbar = [];

  get pageHeader(): string {
    return HelpText[0].committeeFileViewer.pageHeading;
  }

  get pageDescription(): string {
    return HelpText[0].committeeFileViewer.pageDescription;
  }

  get pageSection(): any[] {
    return HelpText[0].committeeFileViewer.section;
  }


  @Watch('$route')
  public OnRouteChange(routeTo: any, routeFrom: any): void {
    this.fileId = this.$route.query.fileId as string;
  }

  private meetingDateTime(d: string): string {
    return moment(this.currentCommittee.meeting.meetingDateTime).format('DD MMM YYYY h:mm A')
  }

  get selectedDocumentName() {
    return this.selectedDocument.name;
  }

  mounted() {
    this.committeeId = this.$route.query.committeeId as string
    this.fileId = this.$route.query.fileId as string;
    this.getMyCommittees();
    this.showNote = this.getCookie('noted').length == 0;
  }

  private getMyPersonalNote() {
    const _meetingId = this.currentCommittee.meeting.id;
    this.apiService.memberApi?.apiV1MemberGetMyPersonalNoteMeetingIdGet(_meetingId)
      .then((res: any) => {
        this.meetingNote = res.data.data || '';
        this.editorContent = this.meetingNote;
        this.showEditor = true;
      })
      .catch((err: any) => {
        console.log(err);
        this.eventCtrl.emit('show-snackbar', 'error', 'Error getting My Committee info: ' + err.response.status);
        this.ai.trackException('Error getting EOI committee info: ' + err);
        this.eventCtrl.emit('isLoadingData', false);
      })
  }

  private checkNote() {
    if (this.editorContent !== this.meetingNote ) {
      this.unsavedNote = true;
    }
  }

  private getMyCommittees() {
    this.myCommittees = store.getters.getMyCommittees;

    if (this.myCommittees == null) {
      // console.log('myCommittees is null');
      this.apiService.memberApi?.apiV1MemberGetMyCommitteesGet()
        .then((res: any) => {
          this.myCommittees = res.data.data;
          store.dispatch('setMyCommittees', this.myCommittees);
          // this.meetingNote = '';
          this.setCurrentFile();
          this.getMyPersonalNote();
          this.eventCtrl.emit('isLoadingData', false);
        })
        .catch((err: any) => {
          console.log(err);
          this.eventCtrl.emit('show-snackbar', 'error', 'Error getting My Committee info: ' + err.response.status);
          this.ai.trackException('Error getting EOI committee info: ' + err);
          this.eventCtrl.emit('isLoadingData', false);
        })
    } else {
      // this.meetingNote = '';
      this.setCurrentFile();
      this.getMyPersonalNote();
    }

  }

  private setCurrentFile() {
    const _thisCommittee = this.myCommittees.filter((c) => {
      return c.id == parseInt(this.committeeId);
    })
    this.currentCommittee = _thisCommittee[0];
    // console.log('currentCommittee: ', this.currentCommittee)
    const _thisFiles = this.currentCommittee.meeting.files.filter((f: any) => {
      return f.id == parseInt(this.fileId);
    })
    this.currentFile = _thisFiles[0];
    this.loadFileList();
    if (this.currentFile !== undefined) {
      this.openFile(this.currentFile);
    } else {
      this.fileId = '-1';
      this.errorOpenFile();
    }

  }

  private errorOpenFile() {
    const ifr = document.getElementById('refDocViewer') as HTMLIFrameElement;
    this.$router.push({ query: { 'fileId': this.fileId, 'committeeId': this.currentCommittee.id } });
    ifr.contentWindow?.location.replace('/no-file-selected');
  }

  private getCookie(cName: string) {
    const name = cName + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }

  private setCookie(cName: string, cValue: string, exDays: number) {
    const d = new Date();
    d.setTime(d.getTime() + (exDays * 24 * 60 * 60 * 1000));
    const expires = "expires=" + d.toUTCString();
    document.cookie = cName + "=" + cValue + ";" + expires + ";path=/";
  }

  private openFile(file: any) {
    this.currentFile = file;
    const ifr = document.getElementById('refDocViewer') as HTMLIFrameElement;
    this.$router.push({ query: { 'fileId': file.id, 'committeeId': this.currentCommittee.id } });
    ifr.contentWindow?.location.replace('/App/Viewer?fileId=' + file.id);
  }

  private loadFileList() {
    // get all agenda and personal files into this.fileList
    const _allFiles = this.currentCommittee.meeting.files || [];

    this.agendaFiles = _allFiles.filter((file: any) => {
      return file.fileTypeName == 'Agenda';
    });
    this.agendaSupportingFiles = _allFiles.filter((file: any) => {
      return file.fileTypeName == 'Agenda Supporting';
    });
    this.agendaSupportingFiles = orderBy(this.agendaSupportingFiles, [(data: any) => data.name.toLowerCase()], ['asc']) as any;
    this.loadPersonalFiles();
  }

  private tapClicked(tab: number) {
    this.activeTab = tab;
  }

  private async uploadAllPersonalMeetingFiles(fileList: Array<File>): Promise<void> {
    const allRequests: Array<Promise<boolean>> = [];
    try {
      if (fileList.length > 0) {
        fileList.forEach((x) => allRequests.push(this.uploadPersonalMeetingFile(x)));
        this.isSubmitting = true;
        await Promise.all(allRequests);
        this.eventCtrl.emit('show-snackbar', 'success', 'File(s) uploaded');
      }
    } catch (err) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on upload file: ' + err);
      this.ai.trackException('Error on upload file: ' + err);
    } finally {
      this.isSubmitting = false;
      // this.getMyPersonalNote();
      this.loadFileList();
    }
  }

  private async uploadPersonalMeetingFile(file: File): Promise<boolean> {
    try {
      const body: PersonalFile = {
        fileName: file.name,
        fileDataUrl: '',
        committeeId: this.committeeId,
        yearId: this.currentCommittee.year.id,
        meetingId: this.currentCommittee.meeting.id
      };

      const fileStr = await this.fileToString(file);
      body.fileDataUrl = fileStr;
      await this.apiService.fileApi?.apiV1FileSavePersonalFilePost(body);
      return Promise.resolve(true);
    } catch (err) {
      return Promise.resolve(false);
    }
  }

  private fileToString(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        return resolve(reader.result as string);
      };

      reader.onabort = () => {
        return reject();
      }

      reader.onerror = () => {
        return reject();
      }
    });
  }

  private showFileDeleteAlert(fileId: number) {
    this.fileToDelete = fileId;
    this.showFileDeleteAlertBox = true;
  }

  private deletePersonalFile() {
    this.apiService.fileApi?.apiV1FileDeletePersonalFileFileIdDelete(this.fileToDelete)
      .then((res: any) => {
        this.eventCtrl.emit('show-snackbar', 'success', 'File successfully deleted');
        this.loadPersonalFiles();
        this.showFileDeleteAlertBox = false;
        this.setCurrentFile();
      })
      .catch((err: any) => {
        console.log(err);
        this.showFileDeleteAlertBox = false;
        this.eventCtrl.emit('show-snackbar', 'error', 'Error on delete file: ' + err.response.status);
        this.ai.trackException('Error on upload file: ' + err);
      })
  }

  private loadPersonalFiles() {
    this.personalMeetingFiles = [];
    this.apiService.fileApi?.apiV1FileGetPersonalFilesMeetingIdGet(this.currentCommittee.meeting.id)
      .then((res: any) => {
        if (res.data.data !== null && res.data.data.length > 0) {
          this.personalMeetingFiles = orderBy(
            res.data.data,
            ['createdOn', 'name'],
            ['desc']
          ) as any;
        }
      })
      .catch((err: any) => {
        console.log(err);
        this.eventCtrl.emit('show-snackbar', 'error', 'Error on getting personal file list: ' + err.response.status);
        this.ai.trackException('Error on upload file: ' + err);
      })
  }

  private saveNoteAndExit(save: boolean) {
    if (save) {
      this.saveNote(true, true);
    } else {
      this.unsavedNote = false;
      this.$router.push({ name: RouteN.CMP.Dashboard.n });
    }
  }

  private saveNote(save: boolean, exit?: boolean) {
    if (!save) {
      this.editorContent = '';
      const _editor: any = this.$refs.quillEditor;
      _editor.setHTML(this.editorContent);
    }
    this.meetingNote = this.editorContent;
    const body = {
      meetingId: this.currentCommittee.meeting.id,
      personalNote: this.meetingNote
    }
    this.apiService.memberApi?.apiV1MemberSaveMyPersonalNotePost(body)
      .then((res: any) => {
        this.unsavedNote = false;
        if (!save) {
          this.eventCtrl.emit('show-snackbar', 'success', 'Personal meeting removed');
        } else {
          this.eventCtrl.emit('show-snackbar', 'success', 'Personal meeting note saved');
        }
        if (exit) {
          this.$router.push({ name: RouteN.CMP.Dashboard.n });
        }

      })
      .catch((err) => {
        this.eventCtrl.emit('show-snackbar', 'error', 'Error on updating Personal meeting note, ' + err.response.status);
        this.ai.trackException('Error on updating personal meeting note: ' + err);
      })

  }


  private openFullscreen(file: any) {
    this.fileSource = '/App/Viewer?fileId=' + file.id;
    this.showFullScreenFileViewer = true;
  }

}

